import React, { CSSProperties, ChangeEvent, forwardRef } from 'react';
import {
  FormGroup,
  FormControl,
  ControlLabel
} from '@devstart/react-bootstrap';
import './select.css';
import ChevronDown2 from '../../../assets/icons/chevron-down-2';

type SelectOptionProps = {
  value: string;
  option: string;
  style?: CSSProperties;
  disabled?: boolean;
};

type StyleTypeProps = 'type-1' | 'type-2';

type SelectSize = 'sm' | 'md';

type SelectProps = {
  label?: string;
  style?: CSSProperties;
  options?: SelectOptionProps[];
  error?: string | null;
  placeholder?: string;
  value?: string;
  selectStyleType?: StyleTypeProps;
  size?: SelectSize;
  onChangeSelect?: (event: ChangeEvent<HTMLSelectElement>) => void;
} & React.HTMLAttributes<HTMLSelectElement> & { [key: string]: unknown };

const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    label,
    style,
    options = [],
    error,
    placeholder,
    value,
    selectStyleType = 'type-1',
    onChangeSelect,
    size,
    ...rest
  },
  ref
): JSX.Element {
  const isPlaceholderSelected = value === '';

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    if (rest.disabled) return;

    if (onChangeSelect) {
      event.preventDefault();
      onChangeSelect(event);
    }

    if (rest.onChange) {
      event.preventDefault();
      rest.onChange(event);
    }
  };

  const getSizeClass = () => {
    if (size === 'sm') return 'select-sm';
    if (size === 'md') return 'select-md';
    return '';
  };

  const filteredOptions = options.filter(opt => !opt.disabled);
  const selectedOption = options.find(opt => opt.value === value);

  return (
    <FormGroup
      className={`select-container input-container input-select-container ${
        isPlaceholderSelected ? 'placeholder-selected' : ''
      }`}
      style={style}
    >
      <ControlLabel>{label}</ControlLabel>

      <div className='select-input'>
        <FormControl
          componentClass='select'
          className={`form-control-select 
            ${selectStyleType === 'type-2' ? 'select-type-2' : ''}
            ${getSizeClass()}
          `}
          placeholder={placeholder}
          value={value}
          ref={ref}
          {...rest}
          onChange={handleChange}
        >
          {placeholder && (
            <option value='' disabled>
              {placeholder}
            </option>
          )}
          {selectedOption && (
            <option value={selectedOption.value} hidden>
              {selectedOption.option}
            </option>
          )}
          {filteredOptions.map(({ option, value, style }) => (
            <option key={value} value={value} style={style}>
              {option}
            </option>
          ))}
        </FormControl>

        <div className='icon-area open-select'>
          <ChevronDown2 />
        </div>
      </div>
      {error && <span className='error-message'>{error}</span>}
    </FormGroup>
  );
});

Select.displayName = 'Select';

export default Select;
